import Controller, { ControllerQueryParam } from '@ember/controller';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { SafeString, htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { IntlService } from 'ember-intl';
import { getLogger } from 'log4javascript';
import { ApplicationModel } from 'login/routes/application';
import FlashMessagesService from 'w-flash-messages/services/flash-messages-service';

const log = getLogger('login');

export default class ApplicationController extends Controller<ApplicationModel> {
  @inject flashMessagesService!: FlashMessagesService;
  @inject intl!: IntlService;

  // queryParam: lec = LoginErrorCode
  override queryParams: readonly ControllerQueryParam[] = ['lec'];
  @tracked lec: string | null = null;

  @action setup(): void {
    window.addEventListener('resize', this.didResize);
    this.didResize();
  }

  @tracked width = 0;
  @tracked height = 0;

  @action didResize(): void {
    const isAndroid = navigator.userAgent.toLowerCase().indexOf('android') > -1;
    if (isAndroid) {
      // for android devices appearance of the virtual keyboard can inadvertently change from portrait
      // mode to landscape mode to avoid this we have to use screen dims (not influenced by the
      // virtual keyboard) (the problem does not occur on IOS, where screen dims even do not seem to
      // work: there we keep using window dims)
      this.width = screen.availWidth;
      this.height = screen.availHeight;
    } else {
      this.width = window.innerWidth;
      this.height = innerHeight;
    }
  }

  private get backgroundUrl(): string {
    const logonParams = this.model.logonParams;
    const wallpaper =
      this.width > this.height ? logonParams.wallpaperLandscape : logonParams.wallpaperPortrait;

    return wallpaper;
  }

  get style(): SafeString {
    const style = this.model.logonParams.useWallpapers
      ? `background-image: url(${this.backgroundUrl});`
      : '';
    return htmlSafe(style);
  }

  @action lecChanged(): void {
    const lec = this.lec;
    if (lec === null || lec.length === 0) {
      return;
    }
    const message = this.intl.t(`login.lec.${this.lec}`);
    let type: 'information' | 'warning' | 'error';
    let duration = 0;
    switch (lec) {
      case 'logout':
        type = 'information';
        duration = 5000;
        break;
      case 'sessionclosed':
      case 'timedout':
      case 'forgotpasswordok':
        type = 'information';
        break;
      case 'invalid':
      case 'unknown':
      case 'expired':
        type = 'warning';
        break;
      case 'duplicateEmail':
      case 'syntaxEmail':
      case 'error':
        type = 'error';
        break;
      default:
        log.error(`Not supported error message: ${this.lec}`);
        return;
    }
    this.flashMessagesService.add({ message, type, duration });
  }
}
